import { useRequiresMedicoAnonymousAccount } from '~/hooks/useIsMedinaviBackendPath';
import { useRouter } from 'next/router';
import { FC, useEffect, useRef, useState } from 'react';
import { Loading } from '~/components/elements/Loading/Loading';
import { medicoUserAuthStorage, medicoUserStorage } from '~/utils/storage/ubieAppStorage';
import { isCurrentUbieAppOrigin } from '~/utils/ubieAppOrigin';
import { resolveMedicoUserAccountGateView, type MedicoUserAccountGateState } from './medico-user-account-gate';

const CREATED_ANONYMOUS_KEY = 'created_anonymous';

const hasCompleteAnonymousSession = (): boolean => {
  if (typeof window === 'undefined') return false;
  const user = medicoUserStorage.get();
  const auth = medicoUserAuthStorage.get();
  // 下流（薬局予約等）は medicoUser.id（PersonID）と非空 accessToken を必須とするため、存在まで見る
  const personId = user?.medicoUser?.id;
  // localStorage の `ubie-auth` は inquiry 側の ubieAuthStorage と共有しており、
  // inquiry でログインしたユーザーでは authSession を持たない形状が入っている。
  // 型上は非 null だが実データでは undefined になりうるため optional chaining する。
  const accessToken = auth?.authSession?.accessToken;
  return personId != null && personId !== '' && accessToken != null && accessToken !== '';
};

const SessionErrorView: FC = () => (
  <div>
    <p>セッションの準備に失敗しました。ページを再読み込みしてください。</p>
    <button type="button" onClick={() => window.location.reload()}>
      再読み込み
    </button>
  </div>
);

// ローカルストレージにアカウントがない場合、ofroにリダイレクトして匿名アカウントを発行する
// 集患サポートおよび薬局予約フロー（Orthrus 匿名 PersonID）で利用する。管理 URL は対象外。
// ref https://www.notion.so/ubie-inc/13c6bbd4e00f806e8de1c3bdf8d943c8
//
// 本コンポーネントは ofro(medico) が同一オリジンの localStorage に書いた
// `medico-user` / `ubie-auth` に依存するため、ubie.app オリジンでのみ動作する。
// `_app` が全ページをラップしている都合上 inquiry.ubie.app（スマホ問診）でもマウントされるが、
// そちらに medico のセッションは存在しないので、何もせず children をそのまま描画する。
export const MedicoUserAccount: FC<React.PropsWithChildren> = (props) => {
  const { asPath } = useRouter();
  const isTargetPath = useRequiresMedicoAnonymousAccount();
  const requiresAnonymousAccount = isTargetPath && isCurrentUbieAppOrigin();
  // 対象パスではセッション確認が終わるまで children を出さない（遷移で ofro redirect を潰さない）
  const [gateState, setGateState] = useState<MedicoUserAccountGateState>(() =>
    requiresAnonymousAccount ? 'pending' : 'ready',
  );
  // 対象パスでセッション完備の ready を一度確認したら true。別パスからの stale ready 誤検知防止用
  const confirmedReadyWithSessionRef = useRef(false);

  useEffect(() => {
    if (!requiresAnonymousAccount) {
      confirmedReadyWithSessionRef.current = false;
      setGateState('ready');
      return;
    }

    if (hasCompleteAnonymousSession()) {
      confirmedReadyWithSessionRef.current = true;
      setGateState('ready');
      return;
    }

    // 未準備の間は children をブロックする（前ページの ready を上書き）
    setGateState('pending');

    const currentUrl = new URL(window.location.href);
    const createdAnonymous = currentUrl.searchParams.has(CREATED_ANONYMOUS_KEY);
    const user = medicoUserStorage.get();

    // アカウント発行後だがユーザを読み込めなかったときに無限ループするのを防ぐ
    if (createdAnonymous && user == null) {
      throw new Error('匿名アカウント発行済みのはずだが、ローカルストレージからユーザを取得できない');
    }

    // user はあるが accessToken が無いなど不完全なセッション
    if (createdAnonymous) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'local') {
        setGateState('local-setup');
      } else {
        setGateState('session-error');
      }
      return;
    }

    // ローカルの場合、sushiroをmedico(ofro)と同じオリジンで動かしてないので、リダイレクトはしない
    if (process.env.NEXT_PUBLIC_APP_ENV === 'local') {
      setGateState('local-setup');
      return;
    }

    currentUrl.searchParams.set(CREATED_ANONYMOUS_KEY, '1');
    const callbackPath = currentUrl.toString().replace(currentUrl.origin, '');
    const medicoUrl = [
      process.env.NEXT_PUBLIC_MEDICO_URL,
      '/for-same-origin-app/create-anonymous',
      `?callbackPath=${encodeURIComponent(callbackPath)}`,
    ].join('');

    // SPA 遷移でキャンセルされないようフルナビゲーションする（pending のまま children は出さない）
    window.location.assign(medicoUrl);
  }, [requiresAnonymousAccount, asPath]);

  // `_app` が全ページを本コンポーネントでラップしているため、render 本体で無条件に localStorage を
  // 読むと /monshin/* など匿名アカウントを必要としないパスまで巻き込む。対象パスに限定する。
  // （requiresAnonymousAccount === false のとき、gate 判定は sessionComplete を参照しない）
  const sessionComplete = requiresAnonymousAccount && hasCompleteAnonymousSession();
  const view = resolveMedicoUserAccountGateView({
    requiresAnonymousAccount,
    gateState,
    sessionComplete,
    confirmedReadyWithSession: confirmedReadyWithSessionRef.current,
  });

  if (view === 'children') {
    return <>{props.children}</>;
  }

  if (view === 'local-setup') {
    return (
      <div>
        ofro(ローカル)を立ち上げて、ローカルストレージの「medico-user」「ubie-auth」をsushiro(ローカル)のローカルストレージに追加してください
      </div>
    );
  }

  if (view === 'session-error') {
    return <SessionErrorView />;
  }

  return <Loading full />;
};
